<script setup lang="ts">
/* =============================================================
   BLOCKED TIME — CREATE
   =============================================================
   Close a berth for a stretch of time, with a reason. Create
   only: a block is two times and a reason, so fixing one is
   remove-and-add on the Availability tab.

   Times are typed in the browser's time zone and stored as
   instants, as everywhere else in the app.

   A copy of admin/src/pages/BlockFormPage.vue, plus the check
   that the berth is the signed-in company's.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import TextField from '@/components/ui/TextField.vue'
import SelectField from '@/components/ui/SelectField.vue'
import Alert from '@/components/ui/Alert.vue'
import { routes, to } from '@/config/routes'
import { getBerth } from '@/lib/berths'
import { createBlock } from '@/lib/blocks'
import { BLOCK_REASONS, fromLocalInput } from '@/lib/bookingLabels'
import { errorMessage } from '@/lib/errors'
import { useUnsavedGuard } from '@/lib/useUnsavedGuard'
import { useAuth } from '@/lib/auth/useAuth'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import type { BlockReason } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { user } = useAuth()

const berthId = computed(() => Number(route.params.id))
useDocumentTitle('Block time')
const backTo = computed(() => to.berthTab(berthId.value, 'availability'))

const blank = { starts_at: '', ends_at: '', reason: '' as BlockReason | '' }
const form = ref({ ...blank })

const berthName = ref('')
const loading = ref(true)
const saving = ref(false)
const notFound = ref(false)
const formError = ref('')
const errors = ref<Record<string, string>>({})

const dirty = computed(() => JSON.stringify(form.value) !== JSON.stringify(blank))
useUnsavedGuard(dirty, saving)

onMounted(async () => {
  try {
    const berth = await getBerth(berthId.value)
    // Another company's berth is readable but not yours to block.
    if (!berth || berth.company_id !== user.value?.companyId) notFound.value = true
    else berthName.value = berth.name
  } catch (e) {
    formError.value = errorMessage(e, 'Could not load the berth.')
  } finally {
    loading.value = false
  }
})

async function onSubmit(): Promise<void> {
  if (saving.value) return
  const next: Record<string, string> = {}
  const start = fromLocalInput(form.value.starts_at)
  const end = fromLocalInput(form.value.ends_at)
  const reason = form.value.reason
  if (!start) next.starts_at = 'Pick a start.'
  if (!end) next.ends_at = 'Pick an end.'
  else if (start && end <= start) next.ends_at = 'Must be after the start.'
  if (!reason) next.reason = 'Choose a reason.'
  errors.value = next
  if (!start || !end || !reason || Object.keys(next).length > 0) return

  saving.value = true
  formError.value = ''
  try {
    await createBlock(berthId.value, { starts_at: start, ends_at: end, reason })
    form.value = { ...blank }
    await router.push(backTo.value)
  } catch (e) {
    formError.value = errorMessage(e, 'Could not add the block.')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <PageHeader
    title="Block time"
    :description="berthName ? `${berthName} · nothing can be booked while the block lasts.` : undefined"
  />

  <p v-if="loading" class="page-status">Loading…</p>

  <Alert v-else-if="notFound" tone="danger">
    That berth does not exist. <RouterLink :to="routes.locations">Back to your locations</RouterLink>.
  </Alert>

  <Panel v-else>
    <form class="form-layout" novalidate @submit.prevent="onSubmit">
      <Alert v-if="formError" tone="danger">{{ formError }}</Alert>

      <div class="form-grid">
        <TextField v-model="form.starts_at" label="From" type="datetime-local" :error="errors.starts_at" />
        <TextField v-model="form.ends_at" label="Until" type="datetime-local" :error="errors.ends_at" />
        <SelectField v-model="form.reason" label="Reason" placeholder="Choose a reason" :error="errors.reason">
          <option v-for="r in BLOCK_REASONS" :key="r.code" :value="r.code">{{ r.label }}</option>
        </SelectField>
      </div>

      <footer class="form-actions">
        <Button variant="ghost" :to="backTo" :disabled="saving">Cancel</Button>
        <Button type="submit" :loading="saving">Block time</Button>
      </footer>
    </form>
  </Panel>
</template>
